import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { KpiTile } from '../api/contract'
import './CommandCenter.css'
import { InfoTip } from './InfoTip'

/** Icon per tile key. Keyed by key, not index, so reordering can't shuffle icons. */
const KPI_ICONS: Record<string, React.ReactNode> = {
  pipelines: <><circle cx="9" cy="8" r="3.2" /><circle cx="16.5" cy="9" r="2.4" /><path d="M2.5 19c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6z" /><path d="M16.5 12.5c2.6 0 4.7 1.9 5 4.5h-4.2c-.1-1.7-.6-3.2-1.4-4.5z" /></>,
  matches: <path d="m12 2.6 2.9 5.9 6.5 1-4.7 4.5 1.1 6.4L12 17.4l-5.8 3 1.1-6.4L2.6 9.5l6.5-1z" />,
  responses: <><path d="M2.5 6.5A2 2 0 0 1 4.5 5h15a2 2 0 0 1 2 1.5L12 13z" /><path d="M2.5 8.6 12 15l9.5-6.4v8.9a2 2 0 0 1-2 2h-15a2 2 0 0 1-2-2z" /></>,
  interviews: <><path d="M4 5h16a1.5 1.5 0 0 1 1.5 1.5V9h-19V6.5A1.5 1.5 0 0 1 4 5z" /><path d="M2.5 10.5h19v8A1.5 1.5 0 0 1 20 20H4a1.5 1.5 0 0 1-1.5-1.5z" /><path d="M7 3v3.5M17 3v3.5" stroke="currentColor" strokeWidth="2" /></>,
  documentation: <><path d="M6 2.5h7L18.5 8v13.5H6z" /><path d="M12.6 2.8v5.4h5.4" fill="#fff" opacity=".55" /><path d="M8.5 12.5h7M8.5 15.5h7M8.5 18h4" stroke="#fff" strokeWidth="1.4" /></>,
  savings: <path d="M14.5 8.4c-.6-1.1-1.7-1.8-3.1-1.8-1.8 0-3.1 1-3.1 2.4 0 3.2 6.4 1.7 6.4 5.1 0 1.5-1.4 2.6-3.3 2.6-1.6 0-2.8-.7-3.4-2M11.7 4.6v14.8" />,
}

const ACCENT_CLASS: Record<KpiTile['accent'], string> = {
  default: 'kpi-i',
  gold: 'kpi-i gold',
  solid: 'kpi-i circ',
}

function KpiCard({ tile }: { tile: KpiTile }) {
  const solid = tile.accent === 'solid'
  return (
    <div className="card kpi">
      <div className={ACCENT_CLASS[tile.accent]} aria-hidden="true">
        <svg
          width="26" height="26" viewBox="0 0 24 24"
          fill={solid ? 'none' : 'currentColor'}
          stroke={solid ? 'currentColor' : undefined}
          strokeWidth={solid ? 2 : undefined}
        >
          {KPI_ICONS[tile.key]}
        </svg>
      </div>
      <div className="kpi-b">
        <div className="kpi-k">{tile.label}</div>
        <div className="kpi-v tnum">{tile.value}</div>
        <div className="kpi-f">
          {tile.note && <span className={`kpi-n${tile.note_is_warning ? ' warn' : ''}`}>{tile.note}</span>}
          <Link className="lnk" to={tile.link_url}>{tile.link_label} →</Link>
        </div>
      </div>
    </div>
  )
}

export function DifferenceBanner() {
  return (
    <section className="diff">
      <div>
        <div className="diff-e">
          NexExec Difference
          <InfoTip tip="difference" />
        </div>
        <h2 className="diff-h">Private Executive Talent. Structured for Better Hiring Decisions.</h2>
        <p className="diff-p">
          NexExec is not a scraped résumé database. Executive profiles are built for company-side
          evaluation with candidate-controlled visibility, hiring signals, team collaboration, and
          Decision Documentation.
        </p>
      </div>
      <Link className="diff-l" to="/help">Learn How NexExec Works &nbsp;→</Link>
      <svg className="shield" width="74" height="74" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2.6" aria-hidden="true">
        <path d="M24 3 7 9.5v13C7 33.5 14.4 41.6 24 45c9.6-3.4 17-11.5 17-22.5v-13z" />
        <rect x="17" y="22" width="14" height="11" rx="2" fill="currentColor" stroke="none" />
        <path d="M20 22v-3a4 4 0 0 1 8 0v3" strokeWidth="2.6" />
      </svg>
    </section>
  )
}

export function CommandCenter() {
  const { data, isPending } = useQuery({ queryKey: ['kpis'], queryFn: () => api.getKpis() })

  return (
    <section className="sec">
      <div className="hd">
        <div>
          <h2 className="h2">Hiring Command Center</h2>
          <p className="sub">Real-time insights to help you move faster and hire with confidence</p>
        </div>
      </div>
      <div className="kpis">
        {isPending && [0, 1, 2, 3, 4, 5].map((i) => (
          <div className="card kpi rc-skeleton" key={i} style={{ minHeight: 96 }} />
        ))}
        {data?.items.map((tile) => <KpiCard key={tile.key} tile={tile} />)}
      </div>
    </section>
  )
}

export function MarketPulse() {
  const { data } = useQuery({ queryKey: ['market-insights'], queryFn: () => api.getMarketInsights() })

  return (
    <section className="sec">
      <div className="hd">
        <div>
          <h2 className="h2">Executive Talent Market Pulse <InfoTip tip="marketPulse" /></h2>
          <p className="sub">Based on NexExec platform activity <InfoTip tip="marketSource" /></p>
        </div>
      </div>
      <div className="pulse">
        {data?.items.map((insight) => (
          <div className="card pc" key={insight.id}>
            <div className="pi" aria-hidden="true">
              {insight.icon === 'trend' ? (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 14.5 8 8.5l3 3 5.5-7" /><path d="M13 4.5h4v4" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7">
                  <circle cx="10" cy="10" r="7.2" />
                  <path d="M2.8 10h14.4M10 2.8c2 2.3 3 5 3 7.2s-1 4.9-3 7.2c-2-2.3-3-5-3-7.2s1-4.9 3-7.2z" />
                </svg>
              )}
            </div>
            <div>
              <div className="pt">{insight.headline}</div>
              <div className="pd">{insight.metric} <span>{insight.metric_note}</span></div>
              <a className="lnk" href={insight.link_url} style={{ display: 'inline-block', marginTop: 9 }}>
                View Market Insight →
              </a>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
